import type { AdStatusApi } from './types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import {
  EXPORT_TASK_BIZ_CODE,
  exportTaskApi,
} from '#/api/modules/common/export-task'
import { requestClient } from '#/api/request'

export const adStatusApi = defineApi('operation', ({ prefix }) => {
  /**
   * 广告信息变更记录历史接口。
   * “查看历史”拉取同一广告的完整变更记录。
   */
  const AD_INFO_CHANGE_RECORDS_HISTORY_PATH =
    'adsGovernorV2/adInfoChangeRecords'

  /**
   * 广告信息变更记录主列表接口。
   * 页面列表只展示每条广告的最新版本，因此后端单独提供 maxVersion 入口。
   */
  const AD_INFO_CHANGE_RECORDS_MAX_VERSION_PATH =
    'adsGovernorV2/adInfoChangeRecords/maxVersion'

  function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {}
  }

  function readMappedScalar(
    record: Record<string, unknown>,
    camelKey: string,
    snakeKey: string,
  ): number | string | undefined {
    const value = record[camelKey] ?? record[snakeKey]
    if (typeof value === 'number' || typeof value === 'string') {
      return value
    }
    return undefined
  }

  function readMappedString(
    record: Record<string, unknown>,
    camelKey: string,
    snakeKey: string,
  ): string | undefined {
    const value = record[camelKey] ?? record[snakeKey]
    return typeof value === 'string' ? value : undefined
  }

  /**
   * 列表项同时兼容 camelCase 与 snake_case。
   * 账户状态相关字段由后端按 Meta 字段名返回，这里收口成页面可直接消费的 camelCase。
   */
  function normalizeAdInfoChangeRecordItem(
    item: unknown,
  ): AdStatusApi.AdInfoChangeRecordItem {
    if (!item || typeof item !== 'object') {
      return item as AdStatusApi.AdInfoChangeRecordItem
    }

    const record = asRecord(item)
    const mappedItem = item as AdStatusApi.AdInfoChangeRecordItem

    return {
      ...mappedItem,
      recommendations: readMappedString(
        record,
        'recommendations',
        'recommendations',
      ),
      accountStatus: readMappedScalar(
        record,
        'accountStatus',
        'account_status',
      ),
      mediaAccountStatusLabel: readMappedString(
        record,
        'mediaAccountStatusLabel',
        'media_account_status_label',
      ),
      disableReason: readMappedScalar(
        record,
        'disableReason',
        'disable_reason',
      ),
      disableReasonLabel: readMappedString(
        record,
        'disableReasonLabel',
        'disable_reason_label',
      ),
      updatedTime: readMappedScalar(record, 'updatedTime', 'updated_time'),
    }
  }

  /**
   * 统一收口列表响应格式转换。
   * 两个查询接口返回结构一致，复用转换逻辑可以保证主列表和历史弹窗分页字段处理一致。
   */
  function normalizeAdInfoChangeRecordsResponse(res: unknown) {
    const payload = asRecord(res)
    const total = Number(payload.total ?? 0)
    const list = Array.isArray(payload.list) ? payload.list : []
    return {
      items: list.map((item) => normalizeAdInfoChangeRecordItem(item)),
      total: Number.isFinite(total) ? total : 0,
    }
  }

  function getMaxVersionAdInfoChangeRecords(
    params: PagingRequest<AdStatusApi.AdInfoChangeRecordQueryCondition>,
  ) {
    return requestClient
      .post(prefix(AD_INFO_CHANGE_RECORDS_MAX_VERSION_PATH), params)
      .then(normalizeAdInfoChangeRecordsResponse)
  }

  function getAdInfoChangeRecords(
    params: PagingRequest<AdStatusApi.AdInfoChangeRecordQueryCondition>,
  ) {
    return requestClient
      .post(prefix(AD_INFO_CHANGE_RECORDS_HISTORY_PATH), params)
      .then(normalizeAdInfoChangeRecordsResponse)
  }

  /**
   * 提交广告数据导出任务，导出文件到导出中心下载。
   * POST /operation/export/submit
   */
  function exportAdInfoChangeRecords(
    condition: AdStatusApi.AdInfoChangeRecordQueryCondition,
  ) {
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_GOVERNOR_V2_AD_INFO_CHANGE_RECORDS,
      { condition },
    )
  }

  /**
   * 获取内容状态页账户类型筛选项。
   * 后端接口只负责返回可选账户类型，这里收口为 Select 标准结构，页面无需关心接口字段。
   */
  function getAccountTypes() {
    return requestClient
      .get<AdStatusApi.AccountTypeOption[]>(prefix('adsGovernor/accountTypes'))
      .then((res) => {
        return (res || []).map((item) => ({
          label: item.label || 'none',
          value: item.value,
        }))
      })
  }

  return {
    getMaxVersionAdInfoChangeRecords,
    getAdInfoChangeRecords,
    exportAdInfoChangeRecords,
    getAccountTypes,
  }
})
